import { storefront } from "../gateway/client";

export type FetchCartOutput = {
  cartId: string;
  token: string;
  freeGiftObjects: {
    edges: Array<{
      node: {
        id: string;
        title: { value: string };
        description: { value: string };
        linkToEnable: { value: string };
        order: { value: string };
        vendors: {
          references: {
            nodes: Array<{
              internalName: { value: string };
            }>;
          };
        };
        priceTier: { value: string };
        priceTierScope: {
          references: {
            nodes: Array<{
              id: string;
              products: {
                nodes: Array<{
                  id: string;
                }>;
              };
            }>;
          };
        };
        group: { value: string };
        type: { value: string };
        products: {
          references: {
            nodes: Array<{
              id: string;
              title: string;
              descriptionHtml: string;
              onlineStoreUrl: string;
              featuredImage: {
                url: string;
              };
              variants: {
                nodes: Array<{
                  id: string;
                  title: string;
                  availableForSale: boolean;
                  selectedOptions: Array<{
                    name: string;
                    value: string;
                  }>;
                  price: {
                    amount: string;
                    currencyCode: string;
                  };
                  compareAtPrice: {
                    amount: string;
                    currencyCode: string;
                  } | null;
                  vendor: {
                    reference: {
                      id: string;
                      title: { value: string };
                    } | null;
                  } | null;
                  image: {
                    url: string;
                  };
                }>;
              };
            }>;
          };
        };
        suggestedCollections: {
          references: {
            nodes: Array<{
              id: string;
              title: string;
              handle: string;
              image: {
                url: string;
              } | null;
              products: {
                nodes: Array<{
                  id: string;
                }>;
              };
            }>;
          };
        };
        suggestedPage: {
          reference: {
            id: string;
            handle: string;
            title: string;
            body: string;
            triggerText: { value: string } | null;
          } | null;
        };
      };
    }>;
  };
  cart: {
    id: string;
    checkoutUrl: string;
    lines: {
      edges: Array<{
        node: {
          id: string;
          quantity: number;
          attributes: Array<{ key: string; value: string }>;
          variant: {
            id: string;
            title: string;
            availableForSale: boolean;
            price: {
              amount: string;
              currencyCode: string;
            };
            image: {
              url: string;
            } | null;
            product: {
              id: string;
              title: string;
              availableForSale: boolean;
              onlineStoreUrl: string;
            };
            vendor: {
              reference: {
                id: string;
                title: { value: string };
              } | null;
            } | null;
          };
        };
      }>;
    };
    cost: {
      subtotalAmount: {
        amount: string;
        currencyCode: string;
      };
      totalAmount: {
        amount: string;
        currencyCode: string;
      };
    };
  };
};

export const FETCH_CART_QUERY = `query getCart($cartId: ID!) {
  freeGiftObjects: metaobjects(first: 128, type: "$app:FreeGift") {
    edges {
      node {
        id
        title: field(key: "title") {
          value
        }
        description: field(key: "description") {
          value
        }          
        linkToEnable: field(key: "linkToEnable") {
          value
        }
        order: field(key: "order") {
          value
        }
        vendors: field(key: "vendors") {
          references(first: 128) {
            nodes {
              ... on Metaobject {
                internalName: field(key: "internalName") {
                  value
                }
              }
            }
          }
        }
        priceTier: field(key: "priceTier") {
          value
        }
        priceTierScope: field(key: "priceTierScope") {
          references(first: 16) {
            nodes {
              ... on Collection {
                id
                products(first: 250) {
                  nodes {
                    id
                  }
                }
              }
            }
          }
        }
        group: field(key: "group") {
          value
        }
        type: field(key: "type") {
          value
        }
        products: field(key: "products") {
          references(first: 16) {
            nodes {
              ... on Product {
                id
                title
                descriptionHtml
                onlineStoreUrl
                featuredImage {
                  url (transform: {
                    maxWidth: 512
                    maxHeight: 512
                    preferredContentType: WEBP
                  })
                }
                variants(first: 32) {
                  nodes {
                    id
                    title
                    availableForSale
                    selectedOptions {
                      name
                      value
                    }
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                    vendor: metafield(namespace: "$app:shipping", key: "vendor"){
                      reference {
                        ... on Metaobject {
                          id
                          title: field(key: "internalName") {
                            value
                          }
                        }
                      }
                    }
                    image {
                      url (transform: {
                        maxWidth: 512
                        maxHeight: 512
                        preferredContentType: WEBP
                      })
                    }
                  }
                }
              }
            }
          }
        }
        suggestedCollections: field(key: "suggestedCollections") {
          references(first: 16) {
            nodes {
              ... on Collection {
                id
                title
                handle
                image {
                  url (transform: {
                    maxWidth: 512
                    maxHeight: 512
                    preferredContentType: WEBP
                  })
                }
                products(first: 250) {
                  nodes {
                    id
                  }
                }
              }
            }
          }
        }
        suggestedPage: field(key: "suggestedPage") {
          reference {
            ... on Page {
              id
              handle
              title
              body
              triggerText: metafield(namespace: "$app:modal", key: "triggerText") {
                value
              }
            }
          }
        }
      }
    }
  }
  cart(id: $cartId) {
    id
    checkoutUrl
    lines(first: 100) {
      edges {
        node {
          id
          quantity
          attributes { key value }
          variant: merchandise {
            ... on ProductVariant {
              id
              title
              availableForSale
              price {
                amount
                currencyCode
              }
              image {
                url (transform: {
                  maxWidth: 512
                  maxHeight: 512
                  preferredContentType: WEBP
                })
              }
              product {
                id
                title
                availableForSale
                onlineStoreUrl
              }
              vendor: metafield(namespace: "$app:shipping", key: "vendor"){
                reference {
                  ... on Metaobject {
                    id
                    title: field(key: "internalName") {
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    cost {
      subtotalAmount {
        amount
        currencyCode
      }
      totalAmount {
        amount
        currencyCode
      }
    }
  }
}`;

/**
 * Fetches the complete cart data including token retrieval and Storefront API data.
 * @param {FetchCartInput} input - The input containing cartId if available.
 * @returns {Promise<FetchCartOutput | null>} The cart data or null if failed.
 */
export async function fetchCart(): Promise<FetchCartOutput > {
  try {
    let cartToken: string;
    // Fetch cart token from /cart.js endpoint
    const response = await fetch('/cart.js?gwp=true', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    }) as Response;

    if (!response.ok) {
      throw new Error('Failed to fetch cart.js');
    }

    const data = await response.json();
    
    if (!data?.token) {
      throw new Error('Cart token not found in response');
    }

    cartToken = data.token;

    // Format the cart ID for Storefront API
    const cartId = 'gid:/' + '/' + 'shopify/Cart/' + cartToken;

    // Fetch cart data from Storefront API
    const query = FETCH_CART_QUERY;
    
    const result = await storefront.request(query, { 
      variables: { cartId } 
    }) as { data: Omit<FetchCartOutput, 'token'> };

    return {
      ...result.data,
      token: cartToken,
      cartId
    };
  } catch (error) {
    console.error('Error fetching cart:', error);
    throw error;
  }
};
